import { For, Show, createEffect, createMemo, createSignal, onCleanup } from 'solid-js';
import { Button } from '~/components/common/buttons/Button';
import { useStyling } from '~/global-contexts/CompanyStyling';
import { useLocalization } from '~/hooks/useLocalization';
import { LanguageCode } from '~/use-cases/types/LanguageCode';
import { clickOutside } from '~/utils/mouseEvents';
import iconZh from '../icons/chinese.svg';
import iconEn from '../icons/english.svg';
import iconMx from '../icons/mexico.svg';

type Option = {
  id: LanguageCode;
  value: LanguageCode;
  label: string;
  img?: string;
  alt?: string;
};

const LANGUAGE_OPTIONS: Option[] = [
  { id: LanguageCode.English, value: LanguageCode.English, label: 'English', img: iconEn, alt: 'English' },
  { id: LanguageCode.Spanish, value: LanguageCode.Spanish, label: 'Español', img: iconMx, alt: 'Español' },
  { id: LanguageCode.Chinese, value: LanguageCode.Chinese, label: '中文', img: iconZh, alt: '中文' },
];

export const LocalizationButton = () => {
  const [isOpen, setIsOpen] = createSignal<boolean>(false);
  let container: HTMLDivElement | undefined;
  const { companyStyling } = useStyling();
  const localization = useLocalization();

  const language = createMemo(
    () =>
      LANGUAGE_OPTIONS.find((option) => option.value === localization.currentLanguage()) ??
      LANGUAGE_OPTIONS.find((option) => option.value === companyStyling()?.language) ??
      LANGUAGE_OPTIONS[0]
  );
  const languageOptions = () => LANGUAGE_OPTIONS;

  const handleOptionClicked = (option: Option) => {
    localization.changeLanguage(option.value);
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  createEffect(() => {
    if (!isOpen() || !container) return;
    onCleanup(clickOutside(container, () => setIsOpen(false)));
  });

  return (
    <div ref={container} class="text-sm text-text-level01 md:relative">
      <Button
        variant="text"
        onClick={handleToggle}
        classList={{ 'bg-primary-color/10 md:bg-primary-color/20': isOpen() }}
        class="flex items-center gap-1 rounded-full border px-3 py-2.5 font-medium uppercase text-secondary-color outline-0 transition focus-within:ring md:border-0 md:bg-gray-50 md:px-4 md:py-3 md:font-semibold">
        <Show when={language()?.img}>
          <img class="size-6" src={language()?.img} alt={language()?.alt || ''} />
        </Show>
        {language()?.value}
      </Button>
      <Show when={isOpen()}>
        <div class="fixed inset-x-0 bottom-0 z-50 bg-black/50 md:hidden" onClick={handleToggle} />
        <ul class="absolute inset-x-0 top-full z-1 divide-y divide-slate-100 border border-slate-200 bg-white px-4 py-2 shadow-lg md:left-auto md:right-0 md:mt-2 md:w-48 md:rounded-md md:px-1">
          <For each={languageOptions()}>
            {(option) => (
              <li
                class="hover:bg-primary-color/10 flex cursor-pointer items-center gap-2 px-1 py-4 transition md:rounded-md md:p-3"
                onClick={() => handleOptionClicked(option)}>
                <Show when={option.img}>
                  <img class="size-6" src={option.img} alt={option.alt || ''} />
                </Show>
                {option.label}
                <Show when={language()?.id === option.id}>
                  <svg
                    class="ml-auto text-primary-color"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.166 14.7L18.4251 5.44141L19.9676 6.9845L9.16818 17.784L7.678 16.2932L7.67527 16.296L3.81836 12.4385L5.3609 10.8954L9.166 14.7Z" />
                  </svg>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
};
